import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { CosignatureQueueResponse, CosignatureQueueSearchParams } from "@/core/types";

import { optionFactory } from "./useCosignatureQueue";

type UsePrefetchCosignatureQueueProps = {
  baseSearchParams: CosignatureQueueSearchParams;
  currentPage: number;
  data: CosignatureQueueResponse | undefined;
  maxAhead?: number;
  registerNextCursor: (page: number, key: Nullable<string>) => void;
};

export function usePrefetchCosignatureQueue({
  baseSearchParams,
  currentPage,
  data,
  maxAhead = Infinity,
  registerNextCursor,
}: UsePrefetchCosignatureQueueProps) {
  const queryClient = useQueryClient();

  useEffect(() => {
    let wasCancelled = false;

    async function prefetch(startKey: string, pageIndex: number) {
      if (wasCancelled) return;

      const searchParamsWithKey = { ...baseSearchParams, start_key: startKey };
      const pageData = await queryClient.fetchQuery(optionFactory(searchParamsWithKey));

      if (wasCancelled) return;

      if (pageData.next_start_key && pageIndex <= currentPage + maxAhead) {
        registerNextCursor(pageIndex, pageData.next_start_key);
        await prefetch(pageData.next_start_key, pageIndex + 1);
      }
    }

    if (data?.next_start_key) {
      // background prefetch — ignore failures, React Query will fetch on demand
      prefetch(data.next_start_key, currentPage + 1).catch(() => {});
    }

    return () => {
      wasCancelled = true;
    };
  }, [data?.next_start_key]); // eslint-disable-line react-hooks/exhaustive-deps
}
